import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiChevronDown, FiZap, FiPhone } = FiIcons;

const faqs = [
  { category: 'General', q: 'What areas do you serve?', a: 'We serve all areas in Baramulla district including Baramulla city, Sopore, Pattan, Rafiabad, Uri, Boniyar, Tangmarg, and surrounding villages.' },
  { category: 'General', q: 'Are you available for emergency electrical repairs?', a: 'Yes! We provide 24/7 emergency electrical services. Call us at +91 99066 69918 and we will respond within the hour.' },
  { category: 'General', q: 'How many years of experience do you have?', a: 'Tayyab Electric Studio has over 13 years of professional experience in electrical installation, automation, and security systems across Kashmir.' },
  { category: 'Services', q: 'What electrical services do you offer?', a: 'We offer complete electrical installation, wiring & rewiring, CCTV systems, smart home automation, solar power, security systems, inverter/UPS installation, networking & structured cabling, and annual maintenance contracts (AMC).' },
  { category: 'Services', q: 'Do you install CCTV cameras for homes and shops?', a: 'Yes, we install CCTV systems for homes, shops, offices, and factories. We provide remote access setup so you can monitor from your phone anywhere in the world.' },
  { category: 'Services', q: 'What is an Annual Maintenance Contract (AMC)?', a: 'Our AMC plans include regular inspections, priority emergency response, free minor repairs, and a dedicated support line. Plans start from ₹999/year for Basic to ₹2999/year for Premium.' },
  { category: 'Services', q: 'Do you install solar panels?', a: 'Yes, we install complete solar power systems including panels, inverters, batteries, and grid-tie systems. We handle everything from site survey to final commissioning.' },
  { category: 'Booking', q: 'How do I book a service?', a: 'You can book through our website\'s Book Appointment page, call us at +91 99066 69918, or WhatsApp us. We typically confirm bookings within 2 hours.' },
  { category: 'Booking', q: 'Is there a charge for site visits?', a: 'Initial site visits for major projects like solar, CCTV, or smart home automation are free. For small repairs, a nominal visit charge may apply which is adjusted in the final bill.' },
  { category: 'Booking', q: 'How quickly can you come for a job?', a: 'For emergencies we respond within 1 hour. For regular bookings we typically schedule within 24-48 hours depending on availability.' },
  { category: 'Pricing', q: 'Do you provide cost estimates before starting work?', a: 'Yes, we always provide a detailed written estimate before starting any work. No hidden charges — the quoted price is the final price.' },
  { category: 'Pricing', q: 'What payment methods do you accept?', a: 'We accept cash, UPI (GPay, PhonePe, Paytm), and bank transfer. Payment is typically collected after job completion for small works.' },
  { category: 'Lucky Draw', q: 'What is the Weekly Lucky Draw?', a: 'Every week we run a free lucky draw where anyone from Baramulla district can enter and win prizes including earphones, cables, free service visits, and more. Winners are announced every Sunday at 8 PM on our Facebook page.' },
  { category: 'Lucky Draw', q: 'How do I enter the Lucky Draw?', a: 'Visit our Lucky Draw page, fill in your details, follow our social media pages, and share with friends. It\'s completely free to enter — no purchase required!' },
];

const categories = ['All', ...new Set(faqs.map(f => f.category))];

const FAQItem = ({ faq, isOpen, onToggle }) => (
  <div className="border border-gray-800 rounded-2xl overflow-hidden hover:border-gray-700 transition-colors">
    <button onClick={onToggle} className="w-full flex items-center justify-between p-5 text-left">
      <span className="text-white font-medium pr-4">{faq.q}</span>
      <motion.div animate={{ rotate: isOpen ? 180 : 0 }} transition={{ duration: 0.2 }}>
        <SafeIcon icon={FiChevronDown} className="text-blue-400 flex-shrink-0" />
      </motion.div>
    </button>
    <AnimatePresence>
      {isOpen && (
        <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.25 }}>
          <div className="px-5 pb-5 text-gray-400 text-sm leading-relaxed border-t border-gray-800/50 pt-4">{faq.a}</div>
        </motion.div>
      )}
    </AnimatePresence>
  </div>
);

const FAQ = () => {
  const [open, setOpen] = useState(null);
  const [cat, setCat] = useState('All');
  const [search, setSearch] = useState('');

  const filtered = faqs.filter(f =>
    (cat === 'All' || f.category === cat) &&
    (!search || f.q.toLowerCase().includes(search.toLowerCase()) || f.a.toLowerCase().includes(search.toLowerCase()))
  );

  return (
    <div className="min-h-screen bg-black pt-20">
      <Helmet>
        <title>FAQ — Tayyab Electric Studio | Baramulla Kashmir</title>
        <meta name="description" content="Frequently asked questions about Tayyab Electric Studio's electrical services, CCTV, solar, smart home automation in Baramulla, Kashmir." />
      </Helmet>

      {/* Hero */}
      <section className="py-16 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-900/20 via-black to-cyan-900/10" />
        <div className="relative max-w-3xl mx-auto px-4 text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <div className="inline-flex items-center space-x-2 bg-blue-600/20 border border-blue-500/30 rounded-full px-4 py-2 text-sm text-blue-300 mb-4">
              <SafeIcon icon={FiZap} /><span>Got Questions?</span>
            </div>
            <h1 className="text-4xl md:text-5xl font-black text-white mb-4">
              Frequently Asked <span className="bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent">Questions</span>
            </h1>
            <p className="text-gray-400 text-lg mb-8">Everything you need to know about our electrical services in Baramulla, Kashmir.</p>
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search questions..."
              className="w-full max-w-md bg-gray-900 border border-gray-700 rounded-2xl px-5 py-3 text-white text-sm focus:border-blue-500 focus:outline-none mx-auto block" />
          </motion.div>
        </div>
      </section>

      <section className="pb-20 max-w-3xl mx-auto px-4">
        {/* Category filter */}
        <div className="flex flex-wrap gap-2 mb-8 justify-center">
          {categories.map(c => (
            <button key={c} onClick={() => setCat(c)}
              className={`px-4 py-1.5 rounded-full text-sm font-medium border transition-all ${cat === c ? 'bg-blue-600 border-blue-600 text-white' : 'border-gray-700 text-gray-400 hover:border-gray-500'}`}>
              {c}
            </button>
          ))}
        </div>

        <div className="space-y-3">
          {filtered.length === 0 ? (
            <p className="text-gray-500 text-center py-8">No questions found. <a href="tel:+919906669918" className="text-blue-400">Call us instead!</a></p>
          ) : filtered.map((faq, i) => (
            <FAQItem key={i} faq={faq} isOpen={open === i} onToggle={() => setOpen(open === i ? null : i)} />
          ))}
        </div>

        {/* Still have questions */}
        <div className="mt-12 bg-gradient-to-br from-blue-900/20 to-cyan-900/10 border border-blue-800/30 rounded-3xl p-8 text-center">
          <h3 className="text-white font-bold text-xl mb-2">Still have questions?</h3>
          <p className="text-gray-400 text-sm mb-5">Our team is available 24/7 for any queries.</p>
          <a href="tel:+919906669918"
            className="inline-flex items-center space-x-2 bg-gradient-to-r from-blue-600 to-cyan-600 text-white px-6 py-3 rounded-2xl font-semibold hover:from-blue-700 hover:to-cyan-700 transition-all">
            <SafeIcon icon={FiPhone} /><span>Call +91 99066 69918</span>
          </a>
        </div>
      </section>
    </div>
  );
};

export default FAQ;
